



















import type {
  Comment,
  CommentMargins,
} from '@getaccept/lib-shared/src/contextual-comments/types/comment';
import type { PropType, Ref } from '@vue/composition-api';
import {
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  toRefs,
  watch,
} from '@vue/composition-api';
import Vue from 'vue';
import type { Recipient } from '../../types/Recipient';
import type { User } from '../../users/types/user';
import { useCommentsHeightsCalculator } from '../composables/comments-heights-calculator';
import { FloatingCommentsHelper } from '../helpers/floating-comments.helper';
import { CommentStatus } from '../types/comment-status';
import type { CurrentUser } from '../types/current-user';
import ConversationCard from './ConversationCard.vue';

export default defineComponent({
  name: 'FloatingComments',
  components: {
    ConversationCard,
  },
  props: {
    comments: {
      type: Array as PropType<Comment[]>,
      default: () => [],
    },
    currentUser: {
      type: Object as PropType<CurrentUser>,
      required: true,
    },
    selectedId: {
      type: String,
      default: '',
    },
    recipients: {
      type: Array as PropType<Recipient[]>,
      required: true,
    },
    sender: {
      type: Object as PropType<User>,
      required: true,
    },
  },

  setup(props, { emit }) {
    // TODO: commentMargins member names could be specified better, desired is top, actual and selection are both margin top
    const commentsMargins: Ref<CommentMargins> = ref({});

    const { commentsHeights, addHeight } = useCommentsHeightsCalculator();

    const resizeObserver = new ResizeObserver(entries => {
      entries.forEach((entry: ResizeObserverEntry) => {
        const { height } = entry.target.getBoundingClientRect();
        const commentId = entry.target.attributes['data-conversation-card-id'].value;
        addHeight(commentId, height);
      });
      setMargins();
      if (selectedId.value) {
        adjustMarginsDuringSelection();
      }
    });

    const { comments, selectedId } = toRefs(props);

    onMounted(() => {
      document
        .querySelectorAll('[data-conversation-card-id]')
        .forEach((element: Element) => resizeObserver.observe(element));
    });

    onBeforeUnmount(() => {
      resizeObserver.disconnect();
    });

    const clickOutside = () => {
      emit('select', '');
    };

    const floatingComments: Ref<HTMLElement> = ref(null);

    const getDesiredTop = (dataCommentId: string): number => {
      const anchor: HTMLElement = document.querySelector(`[data-comment-id="${dataCommentId}"]`);
      if (!anchor) {
        return 0;
      }

      const anchorTop = anchor.getBoundingClientRect().top;
      return anchorTop - floatingComments.value.getBoundingClientRect().top;
    };

    const minimumMargin = 10;

    const setFirstCommentMargins = (commentId: string, margin: number) => {
      Vue.set(commentsMargins.value, commentId, {
        desired: margin,
        actual: margin,
        selection: margin,
      });
    };

    const setMinimumMargins = (commentId: string, desired: number) => {
      Vue.set(commentsMargins.value, commentId, {
        desired,
        actual: minimumMargin,
        selection: minimumMargin,
      });
    };

    const setNewMargins = (commentId: string, newMargin: number, desired: number) => {
      Vue.set(commentsMargins.value, commentId, {
        desired,
        actual: newMargin,
        selection: newMargin,
      });
    };

    const setMargins = () => {
      let occupiedSpace = 0;
      comments.value.forEach(({ id }: Comment, index: number) => {
        const desiredTop = getDesiredTop(id);

        if (index === 0) {
          setFirstCommentMargins(id, desiredTop);
          occupiedSpace = desiredTop + commentsHeights.value[id];
          return;
        }

        const newMargin = desiredTop - occupiedSpace;

        if (newMargin < minimumMargin) {
          setMinimumMargins(id, desiredTop);
          occupiedSpace += minimumMargin + commentsHeights.value[id];
          return;
        }

        setNewMargins(id, newMargin, desiredTop);
        occupiedSpace += newMargin + commentsHeights.value[id];
      });
    };

    const resetCommentsSelectionMargins = () =>
      Object.keys(commentsMargins.value).forEach(
        (id: string) => (commentsMargins.value[id].selection = commentsMargins.value[id].actual)
      );

    const getActualDistanceFromTop = (commentIds: string[], actual: number) =>
      commentIds.reduce(
        (distance: number, id: string) =>
          distance + commentsHeights.value[id] + commentsMargins.value[id].actual,
        0
      ) + actual;

    const getSelectionDistanceFromTop = (commentIds: string[], selection: number) =>
      commentIds.reduce(
        (distance: number, id: string) =>
          distance + commentsHeights.value[id] + commentsMargins.value[id].selection,
        0
      ) + selection;

    const updateMarginForFollowingComments = (
      selectedCommentId: string,
      selectedCommentPositionChange: number
    ) => {
      const commentIds = FloatingCommentsHelper.getCommentIdsAfterSelectedComment(
        selectedCommentId,
        comments.value
      );
      if (!commentIds.length) {
        return;
      }
      commentIds.forEach((id: string) => {
        const previousCommentIds = FloatingCommentsHelper.getReversedCommentIdsBeforeId(
          id,
          comments.value
        );
        const combinedDistance = getSelectionDistanceFromTop(
          previousCommentIds,
          commentsMargins.value[id].actual
        );
        const futureDistance = combinedDistance - selectedCommentPositionChange;
        if (futureDistance === commentsMargins.value[id].desired) {
          return;
        }
        if (
          !FloatingCommentsHelper.willCommentBePushedDown(
            futureDistance,
            commentsMargins.value[id].desired
          )
        ) {
          const marginAdditionToMaintainDesiredDistance =
            commentsMargins.value[id].desired - futureDistance;
          commentsMargins.value[id].selection += marginAdditionToMaintainDesiredDistance;
          return;
        }
        if (
          FloatingCommentsHelper.canDecreaseMargin(
            commentsMargins.value[id].selection,
            minimumMargin
          )
        ) {
          commentsMargins.value[id].selection = FloatingCommentsHelper.decreaseMargin(
            commentsMargins.value[id].selection,
            commentsMargins.value[id].desired,
            futureDistance,
            minimumMargin
          );
        }
      });
    };

    const adjustMarginsDuringSelection = () => {
      resetCommentsSelectionMargins();

      const selectedComment = commentsMargins.value[selectedId.value];
      if (!selectedComment) {
        return;
      }

      const { desired, actual } = selectedComment;

      if (desired === actual) {
        selectedComment.selection = desired;
      }

      const previousCommentIds = FloatingCommentsHelper.getReversedCommentIdsBeforeId(
        selectedId.value,
        comments.value
      );

      const distanceFromTop = getActualDistanceFromTop(previousCommentIds, actual);
      const distanceToRemove = distanceFromTop - desired;

      updateMarginForFollowingComments(selectedId.value, distanceToRemove);

      if (previousCommentIds.length === 0) {
        return;
      }

      let distanceRemaining = distanceToRemove;
      previousCommentIds.forEach((id: string, index: number) => {
        if (distanceRemaining === 0) {
          return;
        }

        if (index + 1 === previousCommentIds.length) {
          commentsMargins.value[id].selection -= distanceRemaining;
          distanceRemaining = 0;
          return;
        }

        if (commentsMargins.value[id].actual === minimumMargin) {
          return;
        }

        const allowedMarginToRemove = FloatingCommentsHelper.getMarginToRemoveFromComment(
          commentsMargins.value[id].actual,
          distanceRemaining,
          minimumMargin
        );

        if (allowedMarginToRemove === 0) {
          return;
        }

        commentsMargins.value[id].selection -= allowedMarginToRemove;
        distanceRemaining -= allowedMarginToRemove;
      });
    };

    // Only handle selected comments that has status 'draft'. Other comment selection will be handle in resize observer when their height changes
    watch(selectedId, (id: string, previousId: string) => {
      const affectedId = id || previousId;
      const comment = comments.value.find(comment => comment.id === affectedId);
      const commentIsInDraft = comment?.status === CommentStatus.Draft;
      if (!comment || !commentIsInDraft) {
        return;
      }
      if (!id) {
        setMargins();
        return;
      }
      adjustMarginsDuringSelection();
    });

    const getMarginTop = (commentId: string) => {
      if (!commentsMargins.value[commentId]) {
        return 0;
      }
      return selectedId.value
        ? commentsMargins.value[commentId].selection
        : commentsMargins.value[commentId].actual;
    };

    return {
      floatingComments,
      getMarginTop,
      clickOutside,

      // Exposed for tests
      commentsHeights,
      commentsMargins,
      minimumMargin,
      setFirstCommentMargins,
      setMinimumMargins,
      setNewMargins,
      resetCommentsSelectionMargins,
      getActualDistanceFromTop,
      getSelectionDistanceFromTop,
      setMargins,
      getDesiredTop,
      updateMarginForFollowingComments,
      adjustMarginsDuringSelection,
    };
  },
});
